import { Controller } from '@hotwired/stimulus'
import { createApp } from 'vue'

export default class extends Controller {
  initialize() {
    const configName = this.element.dataset.config

    this.app = createApp({
      name: 'Search',
      compilerOptions: {
        isCustomElement: tag => tag.includes('-')
      },

      data() {
        return {
          searchInput: '',
          hiddenPanel: true,
          tags: [],
          patterns: [],
          colors: [],
          searchOptions: [],
          searchTemplates: [],
          searchAutocomplete: [],
          dataPath: ''
        }
      },

      computed: {
        searchPlaceholder() {
          return `${this.searchOptions.map(option => option.key).sort((a, b) => a.localeCompare(b)).join(', ')}`
        },

        searchOptionsByAscOrder() {
          return [...this.searchOptions].sort((a, b) => a.key.toLowerCase().localeCompare(b.key.toLowerCase()))
        }
      },

      methods: {
        // **************************
        // INPUT
        // **************************
        displayInputError() {
          const searchContainer = this.$refs.searchContainer;
          searchContainer.classList.add("focus-within:bg-red-100", "focus-within:border-red-300", "ring-2", "ring-red-300");
          setTimeout(() => {
            searchContainer.classList.remove("focus-within:bg-red-100", "focus-within:border-red-300", "ring-2", "ring-red-300");
            this.$refs.searchInput.select()
          }, 500);
        },

        focusInput() {
          this.$refs.searchInputForm.focus()
        },

        getFilteredOptions() {
          const value = this.searchInput.toLowerCase().trim()
          return this.searchOptions
                     .filter(option => option.key.toLowerCase().startsWith(value))
                     .map(option => option['key'])
        },

        // HANDLERS
        async handleKeydownInput(event) {
          if (event.ctrlKey && event.key === 'c') {
            this.tags.length = 0
            return
          }

          switch (event.key) {
            case 'Enter':
            case ':':
              this.handleInputEnter(event)
              break
            case 'Delete':
            case 'Backspace':
              this.handleInputDelete(event)
              break
            case 'Escape':
              this.$refs.searchInputForm.blur()
              break
            default:
          }
        },

        handleInputKeydownTab() {
          if (!this.searchInput.trim()) {
            this.searchAutocomplete.length = 0
            return
          }

          event.preventDefault()

          const results = this.getFilteredOptions()
          this.searchAutocomplete = results

          if (results.length === 1) {
            const key = results[0]
            this.addTagFromOptions(key)
            this.searchAutocomplete.length = 0
          }
        },

        handleInputKeyupDelete() {
          const results = this.getFilteredOptions()

          if (results.length !== this.searchOptions.length) {
            this.searchAutocomplete = results
          } else {
            this.searchAutocomplete.length = 0
          }
        },

        handleInputDelete(event) {
          if (this.searchInput !== '') return

          event.preventDefault()

          if (this.tags.length > 0)
            this.getLastTag().focus()

        },

        async handleInputEnter(event) {
          event.preventDefault()
          const value = this.searchInput.trim()

          if (!value) {
            this.$refs.searchInputForm.blur()
            return
          }

          if (this.searchOptions.some(option => option.key === value)) {
            this.addTag(value)
            this.searchAutocomplete.length = 0
            return
          }

          const key = this.getTagKeyFromValue(value)
          this.addTag(key, value, false)

          this.searchAutocomplete.length = 0
          this.fetchDataToForm()
        },

        // **************************
        // TAGS
        // **************************
        async addTag(key, value='', focus=true) {
          const color = this.getTagColorByKey(key)
          this.tags.push({ color, key, value })
          this.searchInput = ''

          if (focus) {
            await this.$nextTick()
            this.getLastTag().focus()
          }
        },

        async addTagFromOptions(key) {
          if (this.getLastTag() && this.getLastTag()?.textContent.trim() === '') return
          this.addTag(key, '')

          await this.$nextTick()
          this.getLastTag().focus()
          this.searchInput = ''
        },

        addTagFromTemplate(tags) {
          tags.map(tag => this.addTag(tag.key, typeof tag.value === 'function' ? tag.value() : tag.value, false))
          this.focusInput()
          this.fetchDataToForm()
        },

        removeTag(tag_or_index) {
          const index = Number.isInteger(tag_or_index) ? tag_or_index : this.tags.indexOf(tag_or_index)
          if (index < 0) return

          this.tags.splice(index, 1)
          this.focusInput()
          this.fetchDataToForm()
        },

        getTagKeyFromValue(value) {
          const match = this.patterns.find(p => p.regex.test(value))
          return match ? match.key : ''
        },

        getTagColorByKey(key) {
          return this.colors?.find(opt => opt.key === key)?.color || 'green'
        },

        getLastTag() {
          const tags = this.$refs.searchTagValue
          return tags ? tags[tags.length - 1] : null
        },

        focusTag(target) {
          const range = document.createRange()
          const selection = window.getSelection()

          range.selectNodeContents(target)

          selection.removeAllRanges()
          selection.addRange(range)
        },

        // Tag Value
        handleTagValueKeydown(event, tag) {
          const value = event.target.textContent

          switch (event.key) {
            case 'Enter':
              this.handleTagValueEnter(tag, value)
              break
            case 'Backspace':
              this.handleTagValueDelete(tag, value)
              break
            case 'Tab':
              event.preventDefault()
              this.handleTagValueTab(tag)
              break
            default:
          }
        },

        handleTagValueEnter(tag, value) {
          if (value) {
            tag.value = value
            this.fetchDataToForm()
          } else
            this.removeTag(tag)

          this.searchAutocomplete.length = 0
          this.focusInput()
        },

        handleTagValueDelete(tag, value) {
          if (value) return

          this.removeTag(tag)
        },

        handleTagValueTab(tag) {
          if (this.searchAutocomplete.length == 1) {
            this.handleTagValueEnter(tag, this.searchAutocomplete[0])
          }
        },

        handleTagValueBlur(tag) {
          if (tag.value.trim() !== '') return

          this.removeTag(tag)
          this.focusInput()
        },

        async tagValueAutocomplete(tag, content) {
          const searchOption = this.searchOptions.find(option => option.key === tag.key)
          if (!searchOption.autocomplete) return

          if (Array.isArray(searchOption.autocomplete))
            this.searchAutocomplete = searchOption.autocomplete.filter(value => value.startsWith(content))
          else if (typeof searchOption.autocomplete === 'string') {
            const url = URL.parse(searchOption.autocomplete, window.location.origin)
            url.searchParams.set('search', content)
            const response = await fetch(url, { headers: { accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } })
            const data = await response.json()
            this.searchAutocomplete = data.map(obj => obj.username)
          }
          this.showPanel()
        },

        // **************************
        // PANEL / LIST
        // **************************
        togglePanel(display = true) {
          this.hiddenPanel = !display
        },

        showPanel() { this.togglePanel() },

        hidePanel() {
          if(!this.$refs.searchInput.parentNode.matches(':focus-within'))
            this.togglePanel(false)
        },

        handleOptionFocus(value) {
          this.searchInput = value
        },

        focusOption(index) {
          const options = this.$refs.dataOption
          if (options && options.length > index) {
            options[index].focus()
          }
        },

        // **************************
        // DATA
        // **************************
        fetchDataToForm () {
          const url = URL.parse(this.dataPath, window.location.origin)
          this.tags.forEach(({ key, value }) => url.searchParams.append(key, value))

          fetch(url, { headers: { accept: 'text/vnd.turbo-stream.html', 'X-Requested-With': 'XMLHttpRequest' } })
            .then(response => response.text())
            .then((data) => {
              Turbo.renderStreamMessage(data)
            })
        },

        async getConfig(configName) {
          const { Config } = await import(`./config/${configName}.js`)
          this.colors = Config.colors
          this.patterns = Config.patterns
          this.searchOptions = Config.searchOptions
          this.searchTemplates = Config.searchTemplates
          this.tags = Config.default.map(tag => { return { ...tag, color: this.getTagColorByKey(tag.key) } })
          this.dataPath = Config.dataPath
          this.fetchDataToForm()
        }
      },

      mounted() {
        this.getConfig(configName)
      }
    })
  }

  connect() {
    this.app.mount(this.element)
  }

  disconnect() {
    this.app.unmount()
  }
}
